<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { yyyy, zzzz, dldl } from "../../store.svelte";
	import { toast } from "../../lib/toast.svelte.ts";
	import { offlinePhotoStorage } from "../../lib/offlineStorage";
	import Fa from "svelte-fa";
	import { faCamera, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";

	interface Props {
		lw_id: number;
		iama: string;
		expanded?: boolean;
		control?: string;
		found: any;
		clkd?: string | null;
	}

	let {
		iama,
		lw_id,
		clkd = null,
		expanded = $bindable(undefined),
		found = $bindable(),
		control = $bindable(undefined),
	}: Props = $props();

	let subject: string = $state();
	let details: string = $state();
	let submitting: boolean = $state(false);
	let photos: Array<{
		dataUrl: string;
		blobUrl?: string;
		blob?: Blob;
		url?: string;
		caption: string;
		metadata: any;
		pending?: boolean;
	}> = $state([]);
	let uploadingPhotos: boolean = $state(false);
	let fileInput: HTMLInputElement;

	const url: string = yyyy.uri.api;

	let uploadPhotos = async () => {
		uploadingPhotos = true;
		// Only process photos that don't have URLs yet (not already uploaded)
		const photosToUpload = photos.filter((p) => !p.url);

		if (photosToUpload.length === 0) {
			uploadingPhotos = false;
			return photos.filter((p) => p.url); // Return already uploaded photos
		}

		const photoPromises = photosToUpload.map(async (photo) => {
			try {
				// Get presigned URL
				const uploadResponse = await fetch(`${url}/rpc/annotation_photo_upload`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + yyyy.sesh.logtoken.token,
					},
					body: JSON.stringify({
						filename: `photo_${Date.now()}.jpg`,
						filetype: "image/jpeg",
						wireident_param: yyyy.sesh.activeLivewire.wireident,
					}),
				});

				if (!uploadResponse.ok) throw new Error("Failed to get upload URL");

				const uploadData = await uploadResponse.json();

				// Convert dataUrl to blob
				const blob = await fetch(photo.dataUrl).then((r) => r.blob());

				// Upload to S3
				const formData = new FormData();
				Object.entries(uploadData.data.fields).forEach(([key, value]) => {
					formData.append(key, value as string);
				});
				formData.append("file", blob, "photo.jpg");

				try {
					const s3Response = await fetch(uploadData.data.url, {
						method: "POST",
						body: formData,
					});

					// S3 uploads typically return 204 (No Content) on success, not 200
					if (!s3Response.ok && s3Response.status !== 204) {
						throw new Error(`Failed to upload photo: ${s3Response.status}`);
					}
				} catch (error) {
					// Handle CORS errors - if it's a NetworkError, the upload likely succeeded
					if (error instanceof TypeError && error.message.includes("NetworkError")) {
						dldl(
							"Comments",
							"Upload succeeded but CORS blocked response - this is expected",
						);
						// Continue as if successful since the upload actually worked
					} else {
						throw error; // Re-throw other errors
					}
				}

				return { ...photo, url: uploadData.url, pending: false };
			} catch (error) {
				dldl("Comments", `Photo upload failed: ${error}`);
				toast.error("Photo upload failed");
				return photo;
			}
		});

		const newlyUploadedPhotos = await Promise.all(photoPromises);
		uploadingPhotos = false;

		// Combine already uploaded photos with newly uploaded ones
		const alreadyUploaded = photos.filter((p) => p.url);
		return [...alreadyUploaded, ...newlyUploadedPhotos.filter((p) => p.url)];
	};

	let submitComment = async () => {
		submitting = true;

		// Upload photos first (only uploads photos that don't have URLs yet)
		let uploadedPhotos = [];
		if (photos.length > 0) {
			uploadedPhotos = await uploadPhotos();
		}

		// Create annotation
		let this_is_value = iama || "annotation";

		// For new annotations (no device), store location in this_is as JSON
		if (!lw_id && yyyy.pos?.info) {
			this_is_value = JSON.stringify({
				type: "new_annotation",
				location: {
					latitude: yyyy.pos.info.latitude,
					longitude: yyyy.pos.info.longitude,
					accuracy: yyyy.pos.info.accuracy,
					timestamp: Date.now(),
				},
			});
		}

		// Prepare the body object
		let bodyData = {
			lw_id: lw_id,
			this_is: this_is_value,
			wireident: yyyy.sesh.activeLivewire.wireident,
			created_by: yyyy.sesh.tokeninfo.email,
			topic: subject,
			details: details,
		};

		// Add geometry if coordinates are available
		// First try clicked coordinates, then fall back to GPS position
		if (clkd && clkd.lng && clkd.lat) {
			bodyData.g = `POINT(${clkd.lng} ${clkd.lat})`;
		} else if (yyyy.pos?.info?.lng && yyyy.pos?.info?.lat) {
			bodyData.g = `POINT(${yyyy.pos.info.lng} ${yyyy.pos.info.lat})`;
		}

		let _body = JSON.stringify(bodyData);
		let _hdrs = {
			"Content-Type": "application/json",
			Authorization: "Bearer " + yyyy.sesh.logtoken.token,
			Prefer: "return=representation",
		};
		let fetchopts = { method: "post", headers: _hdrs, body: _body };
		const response = await fetch(`${url}/annotated`, fetchopts);

		if (response.ok) {
			let annotation;
			let annotatedId;

			const responseText = await response.text();
			if (responseText.trim()) {
				// Response has content, try to parse it
				try {
					annotation = JSON.parse(responseText);
					annotatedId = annotation[0]?.annotated_id;
				} catch (parseError) {
					dldl("Comments", `Failed to parse server response: ${parseError}`);
					toast.error("Invalid server response");
					submitting = false;
					return;
				}
			} else {
				// Empty response is expected and OK for 200/201
				dldl("Comments", "Received empty response - this is expected");
				annotatedId = null; // We won't be able to link photos
			}

			// Now save photos with annotation ID
			if (uploadedPhotos.length > 0 && annotatedId) {
				for (const photo of uploadedPhotos) {
					await fetch(`${url}/annotation_photos`, {
						method: "POST",
						headers: _hdrs,
						body: JSON.stringify({
							annotated_id: annotatedId,
							url: photo.url,
							caption: photo.caption,
							sensor_data: photo.metadata,
							created_by: yyyy.sesh.tokeninfo.email,
							sort_order: uploadedPhotos.indexOf(photo) + 1,
						}),
					});
				}
			}

			toast.success("Your comment was saved");
			// Clean up blob URLs before resetting
			photos.forEach(photo => {
				if (photo.blobUrl) {
					URL.revokeObjectURL(photo.blobUrl);
				}
			});
			
			// Reset form
			subject = "";
			details = "";
			photos = [];
		} else {
			toast.error(`Something went wrong creating your comment`);
		}

		submitting = false;
	};

	// Helper function to resize image for iOS devices
	const resizeImageForIOS = (dataUrl: string, maxSize: number = 1024): Promise<string> => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => {
				const canvas = document.createElement('canvas');
				let width = img.width;
				let height = img.height;

				// Calculate new dimensions
				if (width > height) {
					if (width > maxSize) {
						height = (height * maxSize) / width;
						width = maxSize;
					}
				} else {
					if (height > maxSize) {
						width = (width * maxSize) / height;
						height = maxSize;
					}
				}

				canvas.width = width;
				canvas.height = height;

				const ctx = canvas.getContext('2d');
				if (!ctx) {
					reject(new Error('Failed to get canvas context'));
					return;
				}

				ctx.drawImage(img, 0, 0, width, height);
				
				// Convert back to data URL with JPEG compression
				const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
				dldl("Comments", `Image resized from ${img.width}x${img.height} to ${width}x${height}`);
				resolve(resizedDataUrl);
			};
			img.onerror = () => reject(new Error('Failed to load image'));
			img.src = dataUrl;
		});
	};

	let handleFileCapture = async (event: Event) => {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];

		if (!file) {
			return;
		}

		dldl("Comments", `Photo captured: ${file.name}, Size: ${file.size}, Type: ${file.type}`);

		// Create blob URL for display (much better for iOS/Safari)
		const blobUrl = URL.createObjectURL(file);
		dldl("Comments", `Created blob URL: ${blobUrl}`);

		// Still need to read as data URL for upload, but will use blob URL for display
		const reader = new FileReader();
		
		reader.onerror = (error) => {
			dldl("Comments", `FileReader error: ${error}`);
		};
		
		reader.onabort = () => {
			dldl("Comments", "FileReader aborted");
		};
		
		reader.onload = async (e) => {
			dldl("Comments", "FileReader.onload triggered");
			try {
				let dataUrl = e.target?.result as string;
				dldl("Comments", `DataURL result exists: ${!!dataUrl}`);
			
			// Detect if we're on iPad/iOS
			const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
			const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
			
			dldl("Comments", `DataURL created, length: ${dataUrl?.length}, starts with: ${dataUrl?.substring(0, 50)}`);
			dldl("Comments", `Device info - iOS: ${isIOS}, Safari: ${isSafari}, UserAgent: ${navigator.userAgent}`);
			
			// On iOS/iPad, resize large images to prevent display issues
			if (isIOS && dataUrl.length > 500000) { // If data URL is larger than ~500KB
				try {
					dldl("Comments", "Resizing image for iOS device due to large size");
					dataUrl = await resizeImageForIOS(dataUrl);
					dldl("Comments", `Image resized, new length: ${dataUrl.length}`);
				} catch (resizeError) {
					dldl("Comments", `Failed to resize image: ${resizeError}`);
					// Continue with original dataUrl
				}
			}

			// Get current location for metadata
			let metadata: any = {
				timestamp: Date.now(),
				filename: file.name,
				size: file.size,
				type: file.type,
				device: {
					isIOS,
					isSafari,
					userAgent: navigator.userAgent
				}
			};

			// Use existing location data if available
			if (yyyy.pos?.info) {
				metadata.location = {
					latitude: yyyy.pos.info.latitude,
					longitude: yyyy.pos.info.longitude,
					accuracy: yyyy.pos.info.accuracy,
				};
			}

			// Handle immediate upload or offline storage
			if (yyyy.isOnline) {
				try {
					const uploadedUrl = await uploadPhotoImmediately(dataUrl, metadata);
					photos = [
						...photos,
						{
							dataUrl,
							blobUrl,
							blob: file,
							caption: "",
							metadata,
							url: uploadedUrl,
							pending: false,
						},
					];
					dldl("Comments", `Photo added to array, total photos: ${photos.length}, blobUrl: ${blobUrl}`);
					toast.success("Photo captured and uploaded");
				} catch (error) {
					dldl("Comments", `Upload failed: ${error}`);
					
					// Store photo offline for sync later
					await storePhotoOffline(dataUrl, metadata);
					
					photos = [
						...photos,
						{
							dataUrl,
							blobUrl,
							blob: file,
							caption: "",
							metadata,
							pending: true,
						},
					];
					dldl("Comments", `Photo added to array (upload failed), total photos: ${photos.length}, blobUrl: ${blobUrl}`);
					toast.warning("Photo saved offline, will upload when online");
				}
			} else {
				// Store photo offline for sync later
				await storePhotoOffline(dataUrl, metadata);
				
				photos = [
					...photos,
					{
						dataUrl,
						blobUrl,
						blob: file,
						caption: "",
						metadata,
						pending: true,
					},
				];
				dldl("Comments", `Photo added offline (no connection) to array, total photos: ${photos.length}, blobUrl: ${blobUrl}`);
				toast.warning("Photo saved offline, will upload when online");
			}
			} catch (error) {
				dldl("Comments", `Error in FileReader.onload: ${error}`);
			}
		};

		reader.readAsDataURL(file);

		// Reset input so same file can be selected again
		input.value = "";
	};

	// Upload photo immediately when online
	const uploadPhotoImmediately = async (
		dataUrl: string,
		metadata: any,
	): Promise<string> => {
		try {
			// Get presigned URL
			const uploadResponse = await fetch(`${url}/rpc/annotation_photo_upload`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
				body: JSON.stringify({
					filename: `photo_${Date.now()}.jpg`,
					filetype: "image/jpeg",
					wireident_param: yyyy.sesh.activeLivewire?.wireident || "new_annotation",
				}),
			});

			if (!uploadResponse.ok) {
				const errorText = await uploadResponse.text();
				throw new Error(`Failed to get upload URL: ${uploadResponse.status} - ${errorText}`);
			}

		const uploadData = await uploadResponse.json();

		// Convert dataUrl to blob
		const blob = await fetch(dataUrl).then((r) => r.blob());

		// Upload to S3
		const formData = new FormData();
		Object.entries(uploadData.data.fields).forEach(([key, value]) => {
			formData.append(key, value as string);
		});
		formData.append("file", blob, "photo.jpg");

		try {
			const s3Response = await fetch(uploadData.data.url, {
				method: "POST",
				body: formData,
			});

			// S3 uploads typically return 204 (No Content) on success, not 200
			if (!s3Response.ok && s3Response.status !== 204) {
				throw new Error(`Failed to upload photo: ${s3Response.status}`);
			}
		} catch (error) {
			// Handle CORS errors - on iOS/Safari, network errors often mean success
			const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
			const isCORSError = error instanceof TypeError && 
				(error.message.includes('NetworkError') || 
				 error.message.includes('Failed to fetch') ||
				 error.message === 'Type error');
			
			if (isCORSError && isIOS) {
				dldl(
					"Comments",
					`Upload likely succeeded despite CORS error: ${error.message}`,
				);
				toast.warning(`CORS handled: ${error.message}`);
				// Continue as if successful since S3 doesn't return proper CORS headers
				// Don't throw, just continue to return success
			} else {
				toast.error(`Real error: ${error.message}`);
				throw error; // Re-throw other errors
			}
		}

		return uploadData.url;
		} catch (error) {
			dldl("Comments", `Upload error details: ${error}`);
			throw error;
		}
	};

	// Store photo offline using IndexedDB
	const storePhotoOffline = async (dataUrl: string, metadata: any) => {
		try {
			// Store original image data using new IndexedDB storage
			const photoId = await offlinePhotoStorage.storePhoto({
				data_url: dataUrl,
				caption: "",
				metadata: metadata,
				wireident: yyyy.sesh.activeLivewire?.wireident || "new_annotation",
				created_by: yyyy.sesh.tokeninfo.email,
			});

			dldl("Comments", "Photo stored in offline database");
		} catch (error) {
			dldl("Comments", `Error storing photo offline: ${error.message}`);
			throw error;
		}
	};

	let deletePhoto = (index: number) => {
		const photo = photos[index];
		// Clean up blob URL to prevent memory leak
		if (photo?.blobUrl) {
			URL.revokeObjectURL(photo.blobUrl);
			dldl("Comments", `Revoked blob URL for deleted photo: ${photo.blobUrl}`);
		}
		photos = photos.filter((_, i) => i !== index);
	};

	onMount(() => {
	console.log('clicked is: ', clkd)

	});

	onDestroy(() => {
		// Clean up all blob URLs when component is destroyed
		photos.forEach(photo => {
			if (photo.blobUrl) {
				URL.revokeObjectURL(photo.blobUrl);
				dldl("Comments", `Revoked blob URL on destroy: ${photo.blobUrl}`);
			}
		});
	});
</script>

<div class="comments-content">
	<div class="form-field">
		<input
			class="input-field"
			type="text"
			placeholder="Summary"
			bind:value={subject}
		/>
	</div>

	<div class="form-field">
		<textarea
			class="textarea-field"
			placeholder="Details of the comment"
			bind:value={details}
		/>
	</div>

	<!-- Photo Section -->
	<div class="form-field">
		<div class="photo-controls">
			<!-- Hidden file input with capture attribute -->
			<input
				bind:this={fileInput}
				type="file"
				accept="image/*"
				capture="environment"
				onchange={handleFileCapture}
				style="display: none;"
			/>

			<!-- Visible button that triggers file input -->
			<button
				type="button"
				class="photo-button"
				onclick={() => {
					dldl("Comments", "add photo clicked");
					fileInput?.click();
				}}
				disabled={submitting || uploadingPhotos}
			>
				<Fa icon={faCamera} />
				Add Photo
			</button>
			{#if photos.length > 0}
				<span class="photo-count"
					>{photos.length} photo{photos.length > 1 ? "s" : ""}</span
				>
			{/if}
		</div>

		<!-- Photo Thumbnails -->
		{#if photos.length > 0}
			<div class="photo-grid">
				{#each photos as photo, index}
					<div class="photo-item {photo.pending ? 'pending' : ''}">
						<img 
							src={photo.blobUrl || photo.dataUrl} 
							alt="Captured photo" 
							class="photo-thumbnail"
							onerror={(e) => {
								dldl("Comments", `Image failed to load for photo ${index}, using ${photo.blobUrl ? 'blobUrl' : 'dataUrl'}`);
								// Fallback to dataUrl if blobUrl fails
								if (photo.blobUrl && photo.dataUrl) {
									const img = e.currentTarget as HTMLImageElement;
									img.src = photo.dataUrl;
									dldl("Comments", "Falling back to dataUrl");
								}
							}}
							onload={() => {
								dldl("Comments", `Image loaded successfully for photo ${index} using ${photo.blobUrl ? 'blobUrl' : 'dataUrl'}`);
							}}
						/>
						{#if photo.pending}
							<div class="pending-overlay">
								<span class="pending-text">Uploading when online</span>
							</div>
						{/if}
						<input
							type="text"
							class="photo-caption-input"
							placeholder="Add caption..."
							bind:value={photo.caption}
							onclick={(e) => e.stopPropagation()}
						/>
						<button
							type="button"
							class="delete-photo"
							onclick={() => deletePhoto(index)}
							title="Delete photo"
						>
							<Fa icon={faTrash} size="0.8x" />
						</button>
					</div>
				{/each}
			</div>
		{/if}
	</div>

	<div class="form-field">
		<button
			class="submit-button"
			onclick={submitComment}
			class:is-loading={submitting || uploadingPhotos}
			disabled={!details || !subject || submitting || uploadingPhotos}
		>
			{uploadingPhotos
				? "Uploading photos..."
				: submitting
					? "Submitting..."
					: "Submit Comment"}
		</button>
	</div>
</div>

<style>
	.comments-content {
		font-size: 0.9rem;
	}

	.form-field {
		margin-bottom: 0.75rem;
		/* padding: 0 0.75rem; */
	}

	.field-label {
		display: block;
		color: #363636;
		font-weight: normal;
		margin-bottom: 0.3rem;
		font-size: 0.9rem;
	}

	.input-field,
	.textarea-field {
		width: 100%;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		padding: 0.5rem;
		font-size: 0.9rem;
		color: #4a4a4a;
		background-color: #fff;
		transition:
			border-color 0.2s,
			box-shadow 0.2s;
		box-sizing: border-box;
	}

	.input-field:focus,
	.textarea-field:focus {
		border-color: #3273dc;
		box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.1);
		outline: none;
	}

	.textarea-field {
		min-height: 100px;
		resize: vertical;
	}

	.submit-button {
		width: 100%;
		padding: 0.5rem 1rem;
		background-color: #3273dc;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 0.9rem;
		cursor: pointer;
		transition:
			background-color 0.2s,
			opacity 0.2s;
	}

	.submit-button:hover:not(:disabled) {
		background-color: #2366d1;
	}

	.submit-button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.submit-button.is-loading {
		position: relative;
		color: transparent;
	}

	.submit-button.is-loading::after {
		content: "";
		position: absolute;
		width: 1em;
		height: 1em;
		top: calc(50% - 0.5em);
		left: calc(50% - 0.5em);
		border: 2px solid #ffffff;
		border-right-color: transparent;
		border-radius: 50%;
		animation: spinner 0.5s linear infinite;
	}

	@keyframes spinner {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	/* Photo Styles */
	.photo-controls {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 0.5rem;
	}

	.photo-button {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		background-color: #48c774;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 0.9rem;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.photo-button:hover:not(:disabled) {
		background-color: #3abb67;
	}

	.photo-button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.photo-count {
		color: #48c774;
		font-size: 0.8rem;
		font-weight: 500;
	}

	.photo-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
		gap: 0.5rem;
		margin-top: 0.5rem;
	}

	.photo-item {
		position: relative;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		padding: 0.25rem;
		background-color: #f9f9f9;
		display: flex;
		flex-direction: column;
	}

	.photo-item.pending {
		border-color: #ff9800;
		background-color: #fff3e0;
	}

	.photo-thumbnail {
		width: 100%;
		height: 80px;
		object-fit: cover;
		border-radius: 2px;
		margin-bottom: 0.25rem;
	}

	.photo-caption-input {
		width: 100%;
		padding: 0.25rem;
		font-size: 0.75rem;
		color: #333;
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px solid #dbdbdb;
		border-radius: 2px;
		margin-top: 0.25rem;
		box-sizing: border-box;
	}

	.photo-caption-input:focus {
		outline: none;
		border-color: #3273dc;
		background-color: #fff;
	}

	.photo-caption-input::placeholder {
		color: #999;
		font-size: 0.7rem;
	}

	.delete-photo {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		background-color: rgba(255, 0, 0, 0.8);
		color: white;
		border: none;
		border-radius: 50%;
		width: 20px;
		height: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		font-size: 0.7rem;
	}

	.delete-photo:hover {
		background-color: rgba(255, 0, 0, 1);
	}

	.pending-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(255, 152, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		z-index: 1;
	}

	.pending-text {
		color: white;
		font-size: 0.7rem;
		text-align: center;
		font-weight: bold;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
	}
</style>
